<!-- 
    
    A pure Vue3 implementation of dynamically displayed tool tips. NO JQUERY used in DOM manipulation/targetting. 

    IMPLEMENTATION:

    <TooltipHelper 
    tooltip-text="Click here to transfer multiple individuals to a new/different contract" 
    position="left">
        <a href="#" @click="ClickedMultiIndividualTransfer" class="actionButton" id="btnMultiTransfer">Multi-Individual Transfer</a>
    </TooltipHelper>

    Slot can include any content, it will be wrapped in a div that allows for the hovering actions to take place. 
    The tooltip itself will be detached from its parent element/node and 'teleport' to the <body> tag.
    See Vue Teleport docs:  https://vuejs.org/guide/built-ins/teleport.html
    This allows the tooltip to be precisely positioned without having to worry about the layout settings applied to the tooltip's 
    initial placement in the DOM (see line 116)

-->

<template>

    <!-- 
        @enter on the transition is called when the transition is completed and the element is displayed. When it becomes visible, 
        the PositionTooltip function is called to position the tooltip relative to the SLOT. 
        
        NOTE: By wrapping the tooltip in a v-if, it is only rendered as HTML when the tooltip is shown, so when the tooltip disappears,
        or on page load, there is no unnecessary HTML being added to the page (HTML is not present on page load, and removed when hidden). 
        This is helpful if you want to use this component on a large list such as a data grid.   
    -->
    <teleport to="body">
        <transition name="fade" mode="out-in" @enter="PositionTooltip">
            <div class="tooltipTextContainer" :class="tooltipPositionCssClass" v-if="showTooltip" tabindex="-1" :style="CSS_TooltipContainer" :id="slotId+'_container'" :ref="slotId+'_container'" >
            
                <div tabindex="-1" ref="divTooltipTextContainer">
                    {{ tooltipText }}
                </div>

            </div>
        </transition>
    </teleport>
   

    <!-- Generic slot to attach hover events to (should be the same exact size and position as the slot element) -->
    <div ref="thisSlot" @mouseover="HoverBegin" @mouseout="HoverEnd" class="slotContainer" :id="customSlotId">
        <slot />
    </div>
    
</template>
  
<script>

import { uuid } from "vue-uuid";
export default {
    name: "TooltipHelper",
    props: {
        tooltipText: {
            type: String,
            default: "Tooltip text",
        },
        position: {
            default: "top",
            type: String,
        },
        customSlotId: {
            default: uuid.v1() + "_tooltipSlot",
            type: String,
        },
    },
    data(){
        return{
            showTooltip : false,
            timer:null,
            CSS_TooltipContainer:"",
            verticalPadding:25,
            horizontalPadding:25,
        }
    },
    computed:{
        //this computed property sets the CSS class according to the position PROP
        tooltipPositionCssClass(){
            switch(this.position){
                case "top": return "tooltipTop";
                case "left":return "tooltipLeft";
                case "right": return "tooltipRight";
                case "bottom":return "tooltipBottom";  
            }

            return "tooltipTop";
        }
    },
    methods:{
        HoverBegin(){

            //allow the user ~half a second to hover over the element before the tooltip appears.
            this.timer = setTimeout(() => {
                this.showTooltip = true; 
            }, 500);

        },
        HoverEnd(){
            //if the hover is ended, clear the timeout (so the tooltip doesn't open anyways), and set show to false
            clearTimeout(this.timer);
            this.showTooltip = false;
        },
        FadeEnter(){
            //console.log("fade entered");
        },
        PositionTooltip(){
            
            //console.log("position tooltip");
            //get the ref to the component's tooltip container
            var thisContainer = this.$refs[this.slotId + '_container'];
            //get the dimensions/position of the SLOT (to allow for precise positioning of the tooltip div)
            var thisSlot = this.$refs.thisSlot;
            var slotRect = thisSlot.getBoundingClientRect();
            var thisSlotOffset = {top: slotRect.top , left: slotRect.left + document.documentElement.scrollLeft};
            var slotWidth =  thisSlot.clientWidth;
            var slotHeight =  thisSlot.clientHeight;
  
            //get the height and width of the tooltip for positioning/ centering
            var myWidth = thisContainer.clientWidth;
            var myHeight = thisContainer.clientHeight;
            
            //create CSS rules for the tooltip container to position it relative to the SLOT and according to whether it needs to be above, below, to the left, or to the right of the SLOT
            switch(this.position){
                case "top":
                    //center horizontally and set vertical position just ABOVE the SLOT element + padding
                    this.CSS_TooltipContainer = {'top': thisSlotOffset.top - myHeight - this.verticalPadding + 'px', 'left':thisSlotOffset.left - myWidth/2 + slotWidth/2 + 'px'};
                    break;
                case "left":
                    //center vertically and set horizontal position to just to the LEFT of the SLOT element + padding
                    this.CSS_TooltipContainer = {'top': thisSlotOffset.top -myHeight/2 + slotHeight/2 + 'px', 'left': thisSlotOffset.left - myWidth - this.horizontalPadding + 'px'}; 
                    break;
                case "right":
                    //center vertically and set horizontal position to just to the RIGHT of the SLOT element + padding
                    this.CSS_TooltipContainer = {'top': thisSlotOffset.top -myHeight/2 + slotHeight/2 + 'px', 'left': thisSlotOffset.left + slotWidth + this.horizontalPadding + 'px' }; 
                    break;
                case "bottom":
                    //center horizontally and set vertical position just BELOW the SLOT element + padding
                    this.CSS_TooltipContainer = {'top': thisSlotOffset.top + slotHeight + this.verticalPadding + 'px', 'left': thisSlotOffset.left - myWidth/2 + slotWidth/2 + 'px'}; 
                    break;
            }
              
        }
    }, 
    unmounted(){
        //console.log("get rid of tooltip");
        clearTimeout(this.timer);
        this.showTooltip = false;
    }
};
</script>

<style scoped>

.tooltipTextContainer{
    position: fixed; 
    display:flex;
    flex-direction: column;
    justify-items:center;
    align-items:center;
    width:min-content;
    z-index:9999;
}
.tooltipTextContainer div{
    padding:5px;
    background:white;
    border:1px solid lightgray;
    border-radius: 5px;
    text-align:center;
    position:relative;
    user-select: none;
    box-shadow: 5px 5px 20px rgba(0,0,0,.2);
    width:250px;
}

/* The following rules create the triangle pointer effect under the tooltip (or to the right, top, or left depending on the tooltip's relative position setting) */
.tooltipTextContainer div:after{ 
    content: "";
    border-top: 1px solid lightgray;
    border-right: 1px solid lightgrey;
    height: 15px;
    width: 15px;
    position: absolute;
    transform: rotate(135deg);
    background: white;
    top: calc(100% - 7px);
    left: 50%;
}
.slotContainer{
    display:inline-block;
    width:100%;
}

.tooltipTop div:after {
    transform: rotate(135deg);
    background: white;
    top: calc(100% - 7px);
    left: 50%;
}
.tooltipBottom div:after {
    transform: rotate(-45deg);
    background: white;
    top: -8px;
    left: 50%;
}

.tooltipLeft div:after {
    transform: rotate(45deg);
    top: calc(50% - 7px);
    left: calc(100% - 7px);
}
.tooltipRight div:after {
    transform: rotate(225deg);
    top: calc(50% - 7px);
    left: -8px;
}

/* The following rules are used to setup the transition animation on the tooltip (fading from 0 opacity to full in .3s) */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}


</style>